import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	ButtonIconComponent,
	ITableCellEmitter,
	ITableConfig,
	ITableHeader,
	ITableRow,
	TableComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize } from 'rxjs/internal/operators/finalize';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { IProcessListPayload, IProcessListResponse, PROCESS_STATUS } from '../../../processes/models/processes.models';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { LoadingService } from '../../../shared/services/loading.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { PanelHelperService } from '../../services/panel-helper.service';
import { PanelCurlService } from '../../services/panel-curl.service';
import { PanelProcessesHelperService } from '../../services/panel-processes-helper.service';
import { PanelProcessesService } from '../../services/panel-processes.service';

@Component({
	selector: 'app-manual-validation-processes',
	imports: [
		TranslatePipe,
		TileComponent,
		ButtonComponent,
		ButtonIconComponent,
		TableComponent,
		ModalCurlRequestComponent,
	],
	templateUrl: './manual-validation-processes.component.html',
	styleUrl: './manual-validation-processes.component.scss',
})
export class ManualValidationProcessesComponent implements OnInit {
	private readonly processStatus = PROCESS_STATUS.PENDING;
	private panelProcessesHelper = inject(PanelProcessesHelperService);
	private panelProcessesService = inject(PanelProcessesService);
	private panelCurlService = inject(PanelCurlService);
	protected rolePermissions = inject(RolePermissionsService);
	private panelHelperService = inject(PanelHelperService);
	private loading = inject(LoadingService);
	private navigationService = inject(NavigationService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	public header: ITableHeader[] = this.panelProcessesHelper.header;
	public config: ITableConfig = this.panelProcessesHelper.getConfig('pending');
	public tableFilters: IProcessListPayload = this.panelProcessesHelper.buildListPayload(this.processStatus);
	public content: ITableRow[] = [];
	public totalPending: number = 0;
	public readonly curlModalOpen = signal(false);
	private requestList: Subscription;

	constructor() {
		effect(() => {
			const rawFilters = this.panelHelperService.currentFilters();
			if (rawFilters) {
				this.reloadWithGlobalFilters(rawFilters);
			}
		});
	}

	ngOnInit(): void {
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.header = this.panelProcessesHelper.header;
			if (this.content.length === 0 && this.config.status?.type === 'empty') {
				const { status } = this.panelProcessesHelper.getConfig('pending');
				this.config = { ...this.config, status };
			}
		});
	}

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public getPendingValidationCurl(): string {
		if (!this.tableFilters) return '';
		return this.panelCurlService.buildPanelProcessesCurl(this.tableFilters);
	}

	public goToInProgressProcesses(event?: Event): void {
		this.navigationService.navigateTo(
			{ routeSegments: ['processes'], extras: { queryParams: { status: this.processStatus } } },
			event,
		);
	}

	private reloadWithGlobalFilters(globalFilters: Record<string, unknown>): void {
		this.tableFilters = this.panelProcessesHelper.buildListPayload(this.processStatus, globalFilters);
		this.getList();
	}

	public getList(): void {
		this.requestList?.unsubscribe();

		this.loading.showTable(this.config, this.header.length, this.tableFilters.per_page ?? 5);
		delete this.config.status;

		this.requestList = this.panelProcessesService
			.listProcesses(this.tableFilters)
			.pipe(
				finalize(() => {
					this.requestList = null;
				}),
			)
			.subscribe({
				next: (response: IProcessListResponse) => {
					this.content = this.panelProcessesHelper.transformRow(response.data);
					this.totalPending = response.meta?.total ?? response.data?.length ?? 0;
					if (this.content.length === 0) {
						const { status } = this.panelProcessesHelper.getConfig('pending');
						this.config.status = status;
					}
				},
				error: (_error: HttpErrorResponse) => {
					this.loading.hideTable(this.config);
					this.config['status'] = { type: 'error' };
				},
				complete: () => {
					this.loading.hideTable(this.config);
				},
			});
	}

	public tableRow(rowClick: ITableRow & { event?: MouseEvent }): void {
		this.goToDetail(rowClick.clickValue, rowClick.event);
	}

	private goToDetail(id: any, event?: Event): void {
		this.navigationService.navigateTo({ routeSegments: ['processes', 'detail', id] }, event);
	}
}
